import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Leaf,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  HeartPulse,
  Award,
  Calendar,
  FileText,
  Bed,
  CheckCircle2,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronRight,
  Activity,
  Stethoscope
} from 'lucide-react';

const THERAPIE_SERVICES = [
  {
    id: 'panchakarma',
    title: 'Panchakarma Detoxification',
    category: 'Purification & Cleansing',
    icon: Sparkles,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-700',
    description: 'Five-stage profound detoxification procedure designed to eliminate deep-seated toxins (Ama) and restore metabolic equilibrium.',
    benefits: ['Cellular rejuvenation', 'Metabolic overhaul', 'Immunity boosting', 'Toxin removal']
  },
  {
    id: 'abhyanga',
    title: 'Abhyanga Body Therapy',
    category: 'Massage & Circulation',
    icon: HeartPulse,
    color: 'from-teal-500 to-cyan-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    textColor: 'text-teal-700',
    description: 'Rhythmic full-body massage using warm medicated herbal oils specifically selected for your unique Vata, Pitta, or Kapha constitution.',
    benefits: ['Joint flexibility', 'Lymphatic drainage', 'Stress reduction', 'Improved skin tone']
  },
  {
    id: 'shirodhara',
    title: 'Shirodhara Mental Wellness',
    category: 'Neurological & Mind',
    icon: Activity,
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    textColor: 'text-cyan-700',
    description: 'A soothing stream of warm herb-infused oil poured continuously onto the forehead (third eye) to calm nervous strain and insomnia.',
    benefits: ['Relieves anxiety & stress', 'Enhances sleep quality', 'Sharpens mental focus', 'Calms nervous system']
  },
  {
    id: 'rasayana',
    title: 'Rasayana Rejuvenation',
    category: 'Vitality & Anti-Aging',
    icon: Award,
    color: 'from-emerald-600 to-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    description: 'Specialized Ayurvedic herbal formulations and vitality regimens tailored to promote longevity, tissue strength, and youthful energy.',
    benefits: ['Tissue restoration', 'Sustained energy', 'Anti-aging properties', 'Enhanced stamina']
  },
  {
    id: 'dietary',
    title: 'Custom Dietary & Herbal Plans',
    category: 'Nutrition & Medicine',
    icon: FileText,
    color: 'from-amber-500 to-emerald-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-800',
    description: 'Practitioner-crafted meal schedules and pure herbal decoction plans synchronized with your active therapy sessions.',
    benefits: ['Dosha balancing meals', 'Organic herb tracking', 'Personalized schedule', 'Digestive health (Agni)']
  },
  {
    id: 'inpatient',
    title: 'Inpatient Residential Care',
    category: 'Rehabilitation & Beds',
    icon: Bed,
    color: 'from-indigo-500 to-teal-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-800',
    description: 'Tranquil residential care centers equipped with comfortable bed allocations, 24/7 vitals monitoring, and specialized therapist care.',
    benefits: ['24/7 Care monitoring', 'Dedicated bed suites', 'Daily therapy sessions', 'Peaceful healing environment']
  }
];

const PLATFORM_FEATURES = [
  {
    icon: Calendar,
    title: 'Seamless Appointment Scheduling',
    description: 'Book and manage therapy sessions or doctor consultations effortlessly with real-time status tracking.'
  },
  {
    icon: Stethoscope,
    title: 'Expert Practitioner Consultations',
    description: 'Connect with certified Ayurvedic physicians and specialized therapists dedicated to your health recovery.'
  },
  {
    icon: FileText,
    title: 'Digital Prescriptions & Herbal Logs',
    description: 'Access complete digital prescriptions, therapy instructions, and herbal remedy guidelines anywhere.'
  },
  {
    icon: Activity,
    title: 'Real-Time Progress Analytics',
    description: 'Track your recovery milestones, therapy completion charts, and health improvements over time.'
  },
  {
    icon: Bed,
    title: 'Smart Bed & Facility Management',
    description: 'Inpatient residential bed tracking and center allocation for intensive healing programs.'
  },
  {
    icon: ShieldCheck,
    title: 'Secure & Confidential Care',
    description: 'Full privacy compliance with protected medical records accessible only to you and your care team.'
  }
];

const METRICS = [
  { value: '10,000+', label: 'Patients Restored' },
  { value: '98.5%', label: 'Recovery Satisfaction' },
  { value: '50+', label: 'Certified Doctors & Therapists' },
  { value: '15+', label: 'Specialized Care Centers' }
];

const TESTIMONIALS = [
  {
    name: 'Ananya Sharma',
    role: 'Panchakarma Patient',
    comment: 'Avartana Care transformed my chronic fatigue and joint pain. The personalized Panchakarma therapy and daily progress tracking made my recovery smooth and transparent.',
    rating: 5,
    avatarBg: 'bg-emerald-500'
  },
  {
    name: 'Rajesh Nair',
    role: 'Shirodhara & Stress Relief Patient',
    comment: 'The Shirodhara treatment cured my long-standing insomnia. Being able to view my doctor’s prescriptions and daily wellness notes online was incredibly convenient.',
    rating: 5,
    avatarBg: 'bg-teal-500'
  },
  {
    name: 'Dr. Meera Kulkarni',
    role: 'Senior Ayurvedic Practitioner',
    comment: 'As a therapist, Avartana Care gives me complete visibility into my patients’ therapy schedules, attendance, and recovery logs in one unified dashboard.',
    rating: 5,
    avatarBg: 'bg-cyan-500'
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-800 flex flex-col font-sans">

      {/* ── NAVBAR ── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-emerald-100/80 shadow-sm transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">

          {/* Brand Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-700 rounded-2xl flex items-center justify-center shadow-md shadow-emerald-200 group-hover:scale-105 transition-transform">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-extrabold bg-gradient-to-r from-emerald-800 via-teal-700 to-cyan-800 bg-clip-text text-transparent">
                Avartana Care
              </span>
              <span className="block text-[11px] font-semibold text-emerald-600 uppercase tracking-widest">
                Ayurveda & Therapy
              </span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-gray-600">
            <button onClick={() => scrollToSection('overview')} className="hover:text-emerald-600 transition-colors">
              Overview
            </button>
            <button onClick={() => scrollToSection('therapies')} className="hover:text-emerald-600 transition-colors">
              Therapies
            </button>
            <button onClick={() => scrollToSection('features')} className="hover:text-emerald-600 transition-colors">
              Platform Features
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="hover:text-emerald-600 transition-colors">
              Testimonials
            </button>
            <button onClick={() => scrollToSection('contact')} className="hover:text-emerald-600 transition-colors">
              Contact
            </button>
          </nav>

          {/* Action Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={handleGetStarted}
              className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 px-4 py-2 rounded-xl hover:bg-emerald-50 transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={handleGetStarted}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold text-sm rounded-xl shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
            >
              Get Started
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-xl text-gray-600 hover:bg-emerald-50"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-100 px-4 pt-2 pb-6 space-y-3">
            <button onClick={() => scrollToSection('overview')} className="block w-full text-left py-2 font-medium text-gray-700 hover:text-emerald-600">
              Overview
            </button>
            <button onClick={() => scrollToSection('therapies')} className="block w-full text-left py-2 font-medium text-gray-700 hover:text-emerald-600">
              Therapies
            </button>
            <button onClick={() => scrollToSection('features')} className="block w-full text-left py-2 font-medium text-gray-700 hover:text-emerald-600">
              Platform Features
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left py-2 font-medium text-gray-700 hover:text-emerald-600">
              Testimonials
            </button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 font-medium text-gray-700 hover:text-emerald-600">
              Contact
            </button>
            <div className="pt-2 flex flex-col gap-2">
              <button
                onClick={handleGetStarted}
                className="w-full py-2.5 border border-emerald-600 text-emerald-700 font-semibold rounded-xl"
              >
                Sign In
              </button>
              <button
                onClick={handleGetStarted}
                className="w-full py-2.5 bg-emerald-600 text-white font-semibold rounded-xl shadow-md"
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </header>


      {/* ── HERO SECTION ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/60 via-teal-50/40 to-slate-50 pt-12 pb-20 lg:pt-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* Left Hero Content */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider shadow-xs">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                <span>Next-Generation Ayurvedic Therapy Platform</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-[1.15]">
                Ancient Healing Wisdom,<br />
                <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-700 bg-clip-text text-transparent">
                  Modern Precision Care
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Welcome to <strong>Avartana Care</strong>—the unified platform for personalized Ayurvedic therapy management, doctor consultations, progress tracking, and residential bed care.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <button
                  onClick={handleGetStarted}
                  className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-base rounded-2xl shadow-xl shadow-emerald-200 hover:shadow-emerald-300 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-3 group"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>

                <button
                  onClick={() => scrollToSection('therapies')}
                  className="w-full sm:w-auto px-7 py-4 bg-white border border-gray-200 text-gray-700 hover:text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50/50 font-bold text-base rounded-2xl transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  Explore Therapies
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Trust Pill Checklist */}
              <div className="pt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-semibold text-gray-600 max-w-lg mx-auto lg:mx-0">
                <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-gray-100 shadow-xs">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Personalized Panchakarma</span>
                </div>
                <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-gray-100 shadow-xs">
                  <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" />
                  <span>Certified Ayurvedic Doctors</span>
                </div>
                <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-gray-100 shadow-xs col-span-2 sm:col-span-1">
                  <CheckCircle2 className="w-4 h-4 text-cyan-500 shrink-0" />
                  <span>Digital Therapy Tracking</span>
                </div>
              </div>
            </div>

            {/* Right Hero Graphic Card */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                
                {/* Decorative background glow */}
                <div className="absolute -top-4 -left-4 w-72 h-72 bg-emerald-300/30 rounded-full blur-3xl" />
                <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-teal-300/30 rounded-full blur-3xl" />

                {/* Glassmorphism Feature Preview Card */}
                <div className="relative bg-white/90 backdrop-blur-xl border border-emerald-100 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
                  
                  {/* Card Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <HeartPulse className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">Live Care Dashboard</h4>
                        <p className="text-xs text-gray-500">Avartana Therapy Tracker</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                      Active Session
                    </span>
                  </div>

                  {/* Therapy Status Snippet */}
                  <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-5 text-white shadow-lg space-y-3">
                    <div className="flex justify-between items-center text-xs opacity-90 font-medium">
                      <span>Today's Treatment Plan</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded-full">Day 5 of 14</span>
                    </div>
                    <h3 className="font-bold text-lg">Panchakarma & Abhyanga</h3>
                    <p className="text-xs text-emerald-100 leading-relaxed">
                      Custom herbal oil formulations applied by Senior Practitioner Dr. Meera.
                    </p>
                    <div className="pt-2">
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Therapy Progress</span>
                        <span>72% Completed</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2">
                        <div className="bg-emerald-300 h-2 rounded-full w-[72%]" />
                      </div>
                    </div>
                  </div>

                  {/* Quick Feature Stats */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 text-center">
                      <p className="text-xs text-gray-500 font-semibold">Scheduled Sessions</p>
                      <p className="text-lg font-bold text-gray-800 mt-0.5">12 Therapies</p>
                    </div>
                    <div className="p-3.5 rounded-xl bg-emerald-50/60 border border-emerald-100 text-center">
                      <p className="text-xs text-emerald-600 font-semibold">Doctor Feedback</p>
                      <p className="text-lg font-bold text-emerald-800 mt-0.5">Optimal Vata</p>
                    </div>
                  </div>

                  {/* Action CTA Button inside Card */}
                  <button
                    onClick={handleGetStarted}
                    className="w-full py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold text-xs rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    Access Your Care Portal <ArrowRight className="w-3.5 h-3.5" />
                  </button>

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ── OVERVIEW OF AVARTANA CARE ── */}
      <section id="overview" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
              Overview & Vision
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Reinventing Ayurvedic Healthcare Management
            </h2>
            <p className="text-gray-600 text-base leading-relaxed">
              <strong>Avartana Care</strong> bridges centuries-old Ayurvedic therapeutic knowledge with state-of-the-art clinical software. We provide end-to-end management for therapy treatments, patient records, doctor scheduling, and residential rehabilitation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80 hover:border-emerald-300 transition-all hover:shadow-lg group">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Leaf className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Traditional Therapy Excellence</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Specialized in authentic Panchakarma, Abhyanga, Shirodhara, and customized herbal treatment regimens guided by experienced practitioners.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80 hover:border-teal-300 transition-all hover:shadow-lg group">
              <div className="w-12 h-12 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Activity className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Real-Time Patient Progress</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Track therapy milestones, daily dosha evaluations, treatment attendance, and prescription adjustments live from your digital dashboard.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/80 hover:border-cyan-300 transition-all hover:shadow-lg group">
              <div className="w-12 h-12 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Bed className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Inpatient & Bed Allocation</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Seamless bed reservation and residential care management for intensive Ayurvedic detoxification and post-surgery rehabilitation.
              </p>
            </div>

          </div>

        </div>
      </section>


      {/* ── THERAPY SERVICES SHOWCASE ── */}
      <section id="therapies" className="py-20 bg-slate-50 border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest bg-emerald-100/70 px-3 py-1 rounded-full border border-emerald-200">
              Therapies & Treatment Services
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Holistic Healing Programs Tailored To You
            </h2>
            <p className="text-gray-600 text-base leading-relaxed">
              Explore our core therapeutic offerings designed to rejuvenate body, mind, and spirit under practitioner guidance.
            </p>
          </div>

          {/* Interactive Therapy Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {THERAPIE_SERVICES.map((therapy) => {
              const IconComp = therapy.icon;
              return (
                <div
                  key={therapy.id}
                  className="bg-white rounded-3xl p-7 border border-gray-200/80 hover:border-emerald-400 hover:shadow-xl transition-all flex flex-col justify-between group"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${therapy.color} text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
                        <IconComp className="w-6 h-6" />
                      </div>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${therapy.bgColor} ${therapy.textColor} border ${therapy.borderColor}`}>
                        {therapy.category}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                      {therapy.title}
                    </h3>

                    <p className="text-gray-600 text-sm leading-relaxed">
                      {therapy.description}
                    </p>

                    <div className="pt-2">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Key Health Benefits:</p>
                      <ul className="grid grid-cols-2 gap-1.5 text-xs font-medium text-gray-700">
                        {therapy.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="pt-6 mt-6 border-t border-gray-100">
                    <button
                      onClick={handleGetStarted}
                      className="w-full text-xs font-bold text-emerald-700 group-hover:text-emerald-800 flex items-center justify-between p-2 rounded-xl hover:bg-emerald-50 transition-colors"
                    >
                      <span>Book Therapy Consultation</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>


      {/* ── PLATFORM FEATURES SECTION ── */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold text-teal-600 uppercase tracking-widest bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
              Platform Features
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Designed For Patients, Doctors & Administrators
            </h2>
            <p className="text-gray-600 text-base leading-relaxed">
              Avartana Care streamlines every touchpoint of Ayurvedic healthcare into a simple, responsive web application.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PLATFORM_FEATURES.map((feature, idx) => {
              const IconComp = feature.icon;
              return (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:border-emerald-200 hover:shadow-lg transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center mb-4">
                    <IconComp className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>

        </div>
      </section>


      {/* ── STATS METRICS BANNER ── */}
      <section className="py-16 bg-gradient-to-r from-emerald-800 via-teal-800 to-cyan-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center divide-x-0 sm:divide-x divide-emerald-700/50">
            {METRICS.map((metric, i) => (
              <div key={i} className="p-4 space-y-1">
                <p className="text-3xl sm:text-4xl lg:text-5xl font-black text-emerald-300">{metric.value}</p>
                <p className="text-sm font-semibold text-emerald-100">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── TESTIMONIALS SECTION ── */}
      <section id="testimonials" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest bg-emerald-100 px-3 py-1 rounded-full border border-emerald-200">
              Patient Stories
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Trusted By Thousands Of Healed Individuals
            </h2>
            <p className="text-gray-600 text-base leading-relaxed">
              Read how Avartana Care helped patients regain physical wellness, mental peace, and vital energy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, idx) => (
              <div key={idx} className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-sm flex flex-col justify-between space-y-6">
                <div className="space-y-4">
                  <div className="flex gap-1 text-amber-400">
                    {[...Array(t.rating)].map((_, i) => (
                      <Sparkles key={i} className="w-4 h-4 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm italic leading-relaxed">
                    "{t.comment}"
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className={`w-10 h-10 rounded-full ${t.avatarBg} text-white font-bold flex items-center justify-center text-sm`}>
                    {t.name[0]}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{t.name}</h4>
                    <p className="text-xs text-gray-500 font-medium">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>


      {/* ── GET STARTED BOTTOM CTA BANNER ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-800 rounded-3xl p-10 sm:p-14 text-white text-center shadow-2xl relative overflow-hidden">
            
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-2xl -ml-10 -mb-10" />

            <div className="relative z-10 space-y-6 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                Ready to Experience Next-Level Ayurvedic Care?
              </h2>
              <p className="text-emerald-100 text-base leading-relaxed">
                Join Avartana Care today to schedule consultations, track your therapy progress, and access customized health solutions.
              </p>

              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={handleGetStarted}
                  className="w-full sm:w-auto px-8 py-4 bg-white hover:bg-emerald-50 text-emerald-800 font-extrabold text-base rounded-2xl shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                >
                  Get Started Now <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ── FOOTER ── */}
      <footer id="contact" className="bg-slate-900 text-slate-400 py-16 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            
            {/* Brand Col */}
            <div className="space-y-4 md:col-span-1">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
                  <Leaf className="w-5 h-5" />
                </div>
                <span className="text-lg font-bold text-white">Avartana Care</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Holistic Ayurvedic therapy management platform uniting traditional healing wisdom with modern healthcare technology.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white text-xs font-bold uppercase tracking-wider mb-4">Quick Links</h4>
              <ul className="space-y-2.5 text-xs">
                <li><button onClick={() => scrollToSection('overview')} className="hover:text-white transition-colors">Overview</button></li>
                <li><button onClick={() => scrollToSection('therapies')} className="hover:text-white transition-colors">Therapies</button></li>
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Platform Features</button></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="hover:text-white transition-colors">Testimonials</button></li>
              </ul>
            </div>

            {/* Therapies */}
            <div>
              <h4 className="text-white text-xs font-bold uppercase tracking-wider mb-4">Specialized Therapies</h4>
              <ul className="space-y-2.5 text-xs">
                <li>Panchakarma Detoxification</li>
                <li>Abhyanga Herbal Massage</li>
                <li>Shirodhara Mental Therapy</li>
                <li>Rasayana Rejuvenation</li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white text-xs font-bold uppercase tracking-wider mb-4">Contact & Support</h4>
              <ul className="space-y-3 text-xs">
                <li className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>+91 (800) 123-4567</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>care@avartanacare.com</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Ayurveda Wellness Center, Bengaluru</span>
                </li>
              </ul>
            </div>

          </div>

          <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
            <p>© 2026 Avartana Care. All rights reserved.</p>
            <div className="flex gap-6">
              <span className="hover:text-slate-400 cursor-pointer">Privacy Policy</span>
              <span className="hover:text-slate-400 cursor-pointer">Terms of Service</span>
              <span className="hover:text-slate-400 cursor-pointer">HIPAA Compliance</span>
            </div>
          </div>

        </div>
      </footer>

    </div>
  );
}
